import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';

export interface ProgressBarProps {
  /** Total number of steps */
  steps: number;
  /** Current step, 1-based */
  currentStep: number;
  /**
   * `continuous` — single track with a proportional fill (web signup).
   * `segmented` — one rounded segment per step (app signup webview).
   */
  variant?: 'continuous' | 'segmented';
  className?: string;
}

function ProgressBar({
  steps,
  currentStep,
  variant = 'continuous',
  className,
}: ProgressBarProps) {
  const safeSteps = Math.max(1, steps);
  const safeCurrentStep = Math.min(Math.max(0, currentStep), safeSteps);

  const progressProps = {
    role: 'progressbar',
    'aria-valuemin': 1,
    'aria-valuemax': safeSteps,
    'aria-valuenow': safeCurrentStep,
    'aria-label': `Step ${safeCurrentStep} of ${safeSteps}`,
  };

  if (variant === 'segmented') {
    return (
      <div
        data-slot="progress-bar"
        {...progressProps}
        className={cn('tw:flex tw:w-full tw:items-center tw:gap-1', className)}
      >
        {Array.from({ length: safeSteps }, (_, index) => (
          <div
            key={index}
            data-slot="segment"
            {...(index < safeCurrentStep && { 'data-active': '' })}
            className={cn(
              'tw:h-[5px] tw:min-w-px tw:flex-1 tw:rounded-full',
              index < safeCurrentStep
                ? 'tw:bg-primary'
                : 'tw:bg-border-strong/80',
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      data-slot="progress-bar"
      {...progressProps}
      className={cn('tw:relative tw:h-1 tw:w-full', className)}
    >
      <div
        data-slot="track"
        className="tw:absolute tw:inset-x-0 tw:top-1/2 tw:h-0.5 tw:-translate-y-1/2 tw:rounded-full tw:bg-border-strong/80"
      />
      <div
        data-slot="fill"
        className="tw:absolute tw:inset-y-0 tw:left-0 tw:rounded-full tw:bg-primary tw:transition-[width]"
        style={{ width: `${(safeCurrentStep / safeSteps) * 100}%` }}
      />
    </div>
  );
}

export { ProgressBar };
